/**
 * ============================================================================
 * Runtime configuration loader
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: load `/config.json` at startup so the same Vite bundle can point
 *          at different backends (dev proxy ↔ ngrok tunnel ↔ real prod gateway)
 *          WITHOUT a rebuild.
 *
 * Shape of /config.json:
 *   {
 *     "api_base": "https://xxx.ngrok-free.dev"   // or "" for the dev proxy
 *   }
 *
 * Deploy flow:
 *   - scripts/deploy-edms.sh writes config.json into the dist/ folder with the
 *     LIVE ngrok URL just before rsync.
 *   - .htaccess marks config.json as no-cache so a tunnel rotation is picked
 *     up on next page load.
 *
 * Dev flow:
 *   - public/config.json ships with {"api_base": ""} so the Vite proxy on
 *     localhost:5173 handles /api/* transparently. Same code path; no
 *     env-time branching.
 * ============================================================================
 */

export interface RuntimeConfig {
  /**
   * Origin of the API server (no trailing slash). Empty string means "use
   * the current page's origin" — i.e. the Vite proxy in dev.
   */
  api_base: string;
}

const DEFAULT: RuntimeConfig = { api_base: '' };

let cached: RuntimeConfig = DEFAULT;
let loaded = false;

/**
 * Fetch /config.json. MUST be called before the first apiFetch.
 * Safe to call multiple times — only the first call hits the network.
 *
 * If the fetch fails (missing file in dev, network error), we fall back to
 * the default and log a single warning. This keeps the dev experience
 * frictionless: no config.json → use the Vite proxy.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (loaded) return cached;
  loaded = true;
  try {
    const res = await fetch('/config.json', {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) {
      console.warn(`[runtimeConfig] /config.json returned ${res.status}; using defaults`);
      return cached;
    }
    const ct = res.headers.get('content-type') ?? '';
    if (!ct.includes('json')) {
      console.warn('[runtimeConfig] /config.json content-type is', ct, '; using defaults');
      return cached;
    }
    const json = (await res.json()) as Partial<RuntimeConfig>;
    cached = {
      api_base: normalizeBase(json.api_base ?? ''),
    };
  } catch (e) {
    console.warn('[runtimeConfig] failed to load /config.json:', e);
  }
  return cached;
}

/** Synchronous accessor — only valid AFTER `loadRuntimeConfig()` resolved. */
export function getRuntimeConfig(): RuntimeConfig {
  return cached;
}

/** Trim trailing slash so callers can always template `${base}/api/v1/...`. */
function normalizeBase(raw: string): string {
  if (!raw) return '';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}
